import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import '../styles/np-theme.css';

interface RouteErrorBoundaryProps {
    children: React.ReactNode;
}

interface RouteErrorBoundaryState {
    error: Error | null;
}

/**
 * Keeps a crash inside one module from blanking the whole app. The header and
 * module rail stay usable, and the user gets a way to retry without a reload.
 */
class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
    state: RouteErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // console.log is silenced in this app (bootstrap/cleanConsole); console.error is not.
        console.error('Route render failed:', error, info.componentStack);
    }

    private retry = () => this.setState({ error: null });

    render() {
        if (!this.state.error) {
            return this.props.children;
        }
        return (
            <div className="np-page min-h-screen">
                <div className="np-theme max-w-xl mx-auto px-4 pt-32 pb-12">
                    <div className="bg-white border border-slate-200 rounded px-6 py-8 text-center" role="alert">
                        <span className="inline-flex items-center justify-center w-12 h-12 rounded bg-red-50 text-red-600">
                            <AlertTriangle size={22} aria-hidden="true" />
                        </span>
                        <h1 className="mt-4">This page ran into a problem</h1>
                        <p className="mt-2 text-sm text-slate-500">
                            Nothing you saved was lost. Try loading the page again; if it keeps happening, reload the
                            browser tab and tell your admin which page it was.
                        </p>
                        <div className="mt-5 flex flex-wrap justify-center gap-2">
                            <button
                                type="button"
                                onClick={this.retry}
                                className="inline-flex items-center gap-2 h-9 px-4 rounded bg-blue-600 text-sm text-white hover:bg-blue-700"
                            >
                                <RotateCcw size={14} aria-hidden="true" /> Try again
                            </button>
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="inline-flex items-center h-9 px-4 rounded border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50"
                            >
                                Reload tab
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default RouteErrorBoundary;
